import React, { useState } from "react";
import { ChevronDown, ChevronUp, Zap, TrendingDown, Users, AlertTriangle, BarChart2, ArrowRightLeft, UserPlus, ShieldCheck } from "lucide-react";
import { BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const fmt  = (n) => n == null ? "—" : Number(n).toLocaleString("en-US", { maximumFractionDigits: 1 });
const fmtM = (n) => n == null ? "—" : n >= 1_000_000
  ? `$${(n / 1_000_000).toFixed(1)}M`
  : n >= 1_000 ? `$${(n / 1_000).toFixed(0)}K` : `$${n}`;

const CONF_CFG = {
  HIGH:   { bg: "bg-emerald-100", text: "text-emerald-700", dot: "bg-emerald-500" },
  MEDIUM: { bg: "bg-amber-100",   text: "text-amber-700",   dot: "bg-amber-500"   },
  LOW:    { bg: "bg-gray-100",    text: "text-gray-500",    dot: "bg-gray-400"    },
};

const SEV_COLOR = {
  Critical: "bg-red-500",
  High:     "bg-orange-400",
  Medium:   "bg-amber-400",
  Low:      "bg-gray-300",
};

const SKILL_COLORS = [
  "#0ea5e9","#8b5cf6","#10b981","#f59e0b","#ef4444",
  "#6366f1","#ec4899","#14b8a6","#f97316","#06b6d4",
];

const SHORT_SKILL_LABELS = {
  "Identity & Access Management": "IAM",
  "Cloud Security":               "Cloud Sec",
  "Vulnerability Management":     "Vuln Mgmt",
  "Incident Response":            "Incident Resp",
  "Data Protection":              "Data Prot",
  "Network Security":             "Network Sec",
  "Application Security":         "App Sec",
  "Security Governance":          "Governance",
  "Endpoint Security":            "Endpoint",
  "Threat Intelligence":          "Threat Intel",
};

// ── Dashboard-style KPI card ──────────────────────────────────────────────────
function WieKpiCard({ label, value, sub, color, progress, badge, tooltip }) {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-5 relative overflow-hidden" title={tooltip}>
      <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: "linear-gradient(90deg, #0ea5e9, #8b5cf6)" }} />
      <div className="text-xs font-medium text-gray-500 mb-1">{label}</div>
      <div className={`text-3xl font-extrabold leading-tight ${color}`}>{value}</div>
      {sub && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
      {progress != null && (
        <div className="mt-3 h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${Math.min(100, Math.max(0, progress))}%`, background: "linear-gradient(90deg, #0ea5e9, #8b5cf6)" }}
          />
        </div>
      )}
      {badge && (
        <span className={`mt-2.5 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold border ${badge.cls}`}>
          {badge.label}
        </span>
      )}
    </div>
  );
}

// ── Workload distribution strip ───────────────────────────────────────────────
function WorkloadDistributionStrip({ skills, mode }) {
  const gapMode = mode === "GAP_ANALYSIS";
  const visible = skills.filter(s => (s.requiredFte || 0) > 0);
  const totalRequired = visible.reduce((sum, s) => sum + (s.requiredFte || 0), 0);
  const totalAvailable = visible.reduce((sum, s) => sum + (s.availableFte || 0), 0);
  const totalGap = Math.max(0, totalRequired - totalAvailable);
  if (!visible.length || totalRequired === 0) return null;
  return (
    <div className="mb-5">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold text-gray-500">FTE Demand Distribution</div>
        <div className="flex items-center gap-3">
          {gapMode && totalAvailable > 0 && (
            <>
              <span className="text-[10px] text-gray-400">
                Available <span className="font-bold text-emerald-700">{fmt(totalAvailable)}</span> FTE
              </span>
              {totalGap > 0 && (
                <span className="text-[10px] font-bold text-red-600">
                  Gap {fmt(totalGap)} FTE
                </span>
              )}
            </>
          )}
          <div className="text-xs font-bold text-gray-700">
            {gapMode ? "Required " : "Total "}
            <span className="text-blue-700">{fmt(totalRequired)}</span>
            <span className="font-normal text-gray-400 ml-0.5">FTE</span>
          </div>
        </div>
      </div>
      {/* Required stacked bar */}
      <div className="flex h-4 rounded-lg overflow-hidden mb-1">
        {visible.map((s, i) => {
          const pct = (s.requiredFte / totalRequired) * 100;
          return (
            <div
              key={s.skillArea}
              style={{ width: `${pct}%`, backgroundColor: SKILL_COLORS[i % SKILL_COLORS.length] }}
              className="transition-all"
              title={`${s.skillLabel ?? s.skillArea}: ${fmt(s.requiredFte)} FTE required`}
            />
          );
        })}
      </div>
      {/* Available bar — only in Gap Analysis when capacity is configured */}
      {gapMode && totalAvailable > 0 && (
        <div className="flex h-2 rounded-lg overflow-hidden mb-2" title="Available capacity">
          {visible.map((s, i) => {
            const availPct = Math.min(100, ((s.availableFte || 0) / totalRequired) * 100);
            return (
              <div
                key={s.skillArea}
                style={{ width: `${(s.requiredFte / totalRequired) * 100}%` }}
                className="relative"
              >
                <div
                  className="absolute inset-y-0 left-0 bg-emerald-400 rounded-sm opacity-80"
                  style={{ width: `${availPct / ((s.requiredFte / totalRequired) * 100) * 100}%` }}
                  title={`${s.skillLabel ?? s.skillArea}: ${fmt(s.availableFte)} FTE available`}
                />
              </div>
            );
          })}
        </div>
      )}
      {gapMode && totalAvailable > 0 && (
        <div className="flex items-center gap-3 mb-2">
          <div className="flex items-center gap-1"><div className="w-3 h-2 rounded-sm bg-blue-500 opacity-80" /><span className="text-[10px] text-gray-400">Required</span></div>
          <div className="flex items-center gap-1"><div className="w-3 h-2 rounded-sm bg-emerald-400" /><span className="text-[10px] text-gray-400">Available</span></div>
        </div>
      )}
      {/* Legend — every skill shows its FTE value */}
      <div className="flex flex-wrap gap-x-4 gap-y-1.5">
        {visible.map((s, i) => (
          <div key={s.skillArea} className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-sm flex-shrink-0" style={{ backgroundColor: SKILL_COLORS[i % SKILL_COLORS.length] }} />
            <span className="text-[10px] text-gray-500">{s.skillLabel ?? s.skillArea}</span>
            <span className="text-[10px] font-bold" style={{ color: SKILL_COLORS[i % SKILL_COLORS.length] }}>
              {fmt(s.requiredFte)}
            </span>
            {gapMode && (s.availableFte || 0) > 0 && (
              <span className="text-[10px] text-emerald-600">/ {fmt(s.availableFte)}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── FTE bar chart (recharts) ──────────────────────────────────────────────────
function FteBarChart({ skills, mode }) {
  const gapMode = mode === "GAP_ANALYSIS";
  const data = skills
    .filter(s => (s.requiredFte || 0) > 0)
    .map((s, i) => ({
      name:      SHORT_SKILL_LABELS[s.skillArea] ?? s.skillArea,
      required:  Number((s.requiredFte  || 0).toFixed(1)),
      available: gapMode ? Number((s.availableFte || 0).toFixed(1)) : undefined,
      color:     SKILL_COLORS[i % SKILL_COLORS.length],
    }));
  if (!data.length) return null;
  return (
    <div className="mb-5">
      <div className="text-xs font-semibold text-gray-500 mb-2">
        Required FTE by Skill Area{gapMode ? " (vs Available)" : ""}
      </div>
      <ResponsiveContainer width="100%" height={180}>
        <BarChart data={data} margin={{ top: 4, right: 8, left: -16, bottom: 40 }} barGap={3}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 10, fill: "#6b7280" }}
            tickLine={false} axisLine={false}
            interval={0}
            angle={-35}
            textAnchor="end"
            dy={4}
          />
          <YAxis tick={{ fontSize: 10, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
          <Tooltip
            formatter={(v, name) => [`${v} FTE`, name]}
            contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #e5e7eb", boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}
          />
          <Bar dataKey="required" name="Required" maxBarSize={28} radius={[4, 4, 0, 0]} fill="#3b82f6" />
          {gapMode && (
            <Bar dataKey="available" name="Available" maxBarSize={28} radius={[4, 4, 0, 0]} fill="#16a34a" />
          )}
        </BarChart>
      </ResponsiveContainer>
      {gapMode && (
        <div className="flex items-center gap-4 mt-1">
          <div className="flex items-center gap-1.5 text-[10px] text-gray-500">
            <div className="w-3 h-2 rounded-sm bg-blue-400" />Required
          </div>
          <div className="flex items-center gap-1.5 text-[10px] text-gray-500">
            <div className="w-3 h-2 rounded-sm" style={{ backgroundColor: "#16a34a" }} />Available
          </div>
        </div>
      )}
    </div>
  );
}

// ── Methodology panel ─────────────────────────────────────────────────────────
function MethodologyPanel({ executive, methodologyVersion, parametersJson }) {
  const [open, setOpen] = useState(false);

  // Parse parameters if available
  const params = (() => { try { return parametersJson ? JSON.parse(parametersJson) : null; } catch { return null; } })();
  const amf    = params?.amf ?? 0.10;
  const amfPct = Math.round((1 - amf) * 100);
  const maturity = params?.automationMaturity ?? "High";

  return (
    <div className="mt-3 rounded-xl border border-gray-200 overflow-hidden">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-gray-600">Model Methodology &amp; Assumptions</span>
          {methodologyVersion && (
            <span className="text-[10px] px-1.5 py-0.5 bg-gray-200 text-gray-500 rounded-full font-mono">{methodologyVersion}</span>
          )}
        </div>
        {open ? <ChevronUp className="w-3.5 h-3.5 text-gray-400" /> : <ChevronDown className="w-3.5 h-3.5 text-gray-400" />}
      </button>
      {open && (
        <div className="px-4 py-3 bg-white space-y-3">
          {/* Workforce formula */}
          <div>
            <div className="text-xs font-bold text-gray-700 mb-1">FTE Calculation Formula</div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 font-mono text-[10px] text-gray-700 leading-relaxed">
              effort = BaseHours × SeverityMult × <span className="text-blue-700 font-bold">{amf.toFixed(2)}</span> × 1.10 × 1.15<br />
              Required FTE = Σ [ effort ÷ min(timeline, severity SLA) ] ÷ 109 h/month
            </div>
            <div className="text-xs text-gray-500 mt-1.5 space-y-0.5">
              <p>
                <span className="font-medium">Automation Maturity Factor:</span>{" "}
                <span className="font-semibold text-blue-700">{amf.toFixed(2)} ({maturity})</span>{" "}
                — analysts are estimated to perform approximately {Math.round(amf * 100)}% of total remediation effort directly.
                The remainder is accelerated through automation, tooling, and established workflows.
                Configurable in Team Setup.
              </p>
              <p><span className="font-medium">SeverityMult:</span> Critical 1.5× · High 1.25× · Medium 1.0× · Low 0.75× — Axaris benchmark defaults, configurable per organization</p>
              <p><span className="font-medium">Severity SLA (deadline cap):</span> each finding must be cleared within the sooner of the selected timeline or its severity window — Critical 1mo · High 2mo · Medium 4mo · Low 8mo. A longer timeline lowers required FTE, but urgent work plateaus (a Critical can't be deferred past ~1 month).</p>
              <p><span className="font-medium">1.10</span> coordination overhead (cross-team/vendor dependency) · <span className="font-medium">1.15</span> documentation overhead (tickets, evidence, reports)</p>
              <p>
                <span className="font-medium">109 h/month</span> effective FTE hours:{" "}
                173h gross − 25h meetings − 15h training − 12h context-switching − 12h PTO prorate
              </p>
            </div>
          </div>
          {/* Exposure */}
          <div>
            <div className="text-xs font-bold text-gray-700 mb-1">Financial Exposure Estimate Methodology</div>
            <div className="text-xs text-gray-600 space-y-0.5">
              <p>
                <span className="font-medium">Scenario-based aggregation (v2.0):</span>{" "}
                Each skill area is treated as one breach scenario. Finding count affects breach <em>probability</em> via a severity-weighted multiplier — not the cost of the incident. Prevents per-finding additive inflation.
              </p>
              <p>
                <span className="font-medium">Formula:</span>{" "}
                ScenarioCost = AvgBreachCost × SkillMultiplier × AdjustedProbability × WeightedSeverityMult
              </p>
              <p>
                <span className="font-medium">Probability multiplier:</span>{" "}
                1 + ln(1 + SeverityWeight / ReferenceBenchmark) — logarithmic growth, no hard cap.
                Each additional finding has diminishing marginal probability impact. The only ceiling is 0.95 (absolute probability maximum).
                SeverityWeight = Critical×4 + High×3 + Medium×2 + Low×1.
              </p>
              <p>
                <span className="font-medium">Range:</span>{" "}
                Lower Estimate (60% of central) · Upper Estimate (180% of central) — reflects variance in breach cost outcomes, not statistical percentiles.
              </p>
              <p>Sources: IBM Cost of a Data Breach 2023 ($4.45M baseline) · Verizon DBIR 2023 frequency weights (adapted per domain)</p>
              <p className="text-gray-400 italic">Treat as order-of-magnitude estimates. Not actuarial forecasts.</p>
            </div>
          </div>
          {/* Confidence */}
          <div>
            <div className="text-xs font-bold text-gray-700 mb-1">Confidence Scoring (v2.1 — 3 Scored Dimensions)</div>
            <div className="text-xs text-gray-600 space-y-0.5">
              <p>3 scored dimensions (33% each) — all reflect data configurable on day one:</p>
              <p>1. <span className="font-medium">Data completeness</span> — ≥8 findings with varied severity = full; ≥3 findings = partial</p>
              <p>2. <span className="font-medium">Skill mapping</span> — ≥5 findings to trust category assignment</p>
              <p>3. <span className="font-medium">Capacity configured</span> — FTE entered for this skill area</p>
              <p>
                <span className="font-semibold text-emerald-700">HIGH</span> ≥85% ·{" "}
                <span className="font-semibold text-amber-700">MEDIUM</span> ≥45% ·{" "}
                <span className="font-semibold text-gray-500">LOW</span> &lt;45%
              </p>
              <p className="text-gray-400">
                <span className="font-medium text-emerald-700">✓ Validated</span> badge shown separately in each skill's drilldown when the org has compared forecast outputs to actual remediation data. Improves trust — does not change the score.
              </p>
            </div>
          </div>
          {/* Dedup */}
          <div>
            <div className="text-xs font-bold text-gray-700 mb-1">Deduplication</div>
            <div className="text-xs text-gray-600">
              Findings with identical normalised title + category across modules are merged into one canonical finding.
              "Security Control Gap: X" is treated as equivalent to "X". Duplicate count shown in each row.
            </div>
          </div>
          {/* Assumptions & Limitations */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
            <div className="text-xs font-bold text-amber-800 mb-1">Assumptions &amp; Limitations</div>
            <div className="text-xs text-amber-700 space-y-1">
              <p>
                FTE estimates assume analysts spend {amfPct}% of remediation time using automation/playbooks ({maturity} maturity).
                This assumption is configurable in Team Setup and should be validated against your team's actual workflows.
              </p>
              <p>
                Financial exposure estimates are illustrative ranges, not actuarial loss projections.
                They are useful for prioritisation and executive communication, not insurance or regulatory filings.
              </p>
              <p>
                Skill area mapping is keyword-based. Findings that don't match known categories are assigned to best-fit skill areas.
                Verify mapping quality in the skill drilldown.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── P3: Top Risk-Reduction Actions ────────────────────────────────────────────
function ActionRow({ a, rank }) {
  const [expanded, setExpanded] = useState(false);
  // subActions contains ALL merged control names; show the expand button whenever there are any
  const hasSubs   = a.subActions?.length > 0;
  const subCount  = a.subActions?.length ?? 0;
  const multiSrc  = a.source === "Multiple Sources";
  return (
    <div className="rounded-lg border border-gray-100 hover:border-gray-200 overflow-hidden">
      <div className="flex items-start gap-3 px-3 py-2 hover:bg-gray-50">
        <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
          {rank}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
            <span className="text-xs font-semibold text-gray-900">{a.actionName}</span>
            {hasSubs && (
              <button
                title="Grouped controls addressing the same security outcome"
                onClick={() => setExpanded(v => !v)}
                className="text-[10px] text-blue-600 bg-blue-50 border border-blue-100 px-1.5 py-0.5 rounded-full flex-shrink-0 hover:bg-blue-100 transition-colors"
              >
                {subCount} controls {expanded ? "▲" : "▼"}
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap text-[10px] text-gray-500">
            <span className={`px-1.5 py-0.5 rounded-full text-white ${SEV_COLOR[a.severity] ?? "bg-gray-400"} font-semibold`}>
              {a.severity}
            </span>
            <span className="font-medium text-blue-700">{a.skillLabel}</span>
            <span>·</span>
            {/* One-time remediation effort (not the sustained Required FTE headline) */}
            <span
              title={`${fmtM(a.exposureReduction)} exposure ÷ ${fmt(a.requiredHours)}h effort = ${fmtM(a.riskReductionScore)} exposure reduced per analyst hour`}
              className="cursor-help border-b border-dotted border-gray-300"
            >
              {fmt(a.requiredHours)}h effort
            </span>
            <span>·</span>
            <span className={multiSrc ? "text-purple-500 font-medium" : "text-gray-400"}>
              {a.source}
            </span>
          </div>
        </div>
        {/* Fix 3: show ROI score inline next to exposure */}
        <div className="text-right flex-shrink-0">
          <div className="text-xs font-bold text-emerald-700">↓ {fmtM(a.exposureReduction)}</div>
          {(a.riskReductionScore ?? 0) > 0 && (
            <div className="text-[10px] font-medium text-emerald-600">{fmtM(a.riskReductionScore)} exposure / analyst hour</div>
          )}
          <div className="text-[10px] text-gray-400">{a.timeToImplementDays}d SLA</div>
        </div>
      </div>
      {hasSubs && expanded && (
        <div className="bg-blue-50 border-t border-blue-100 px-4 py-2 space-y-1">
          <div className="text-[10px] font-semibold text-blue-600 uppercase tracking-wider mb-1">
            {subCount} controls{multiSrc ? " · Multiple Sources" : a.source ? ` · ${a.source}` : ""}:
          </div>
          {a.subActions.map((name, j) => (
            <div key={j} className="flex items-center gap-1.5 text-[10px] text-blue-800">
              <span className="w-1 h-1 rounded-full bg-blue-400 flex-shrink-0" />
              {name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TopActionsPanel({ actions }) {
  const [open, setOpen] = useState(false);
  if (!actions?.length) return null;
  return (
    <div className="mb-4 rounded-xl border border-blue-200 overflow-hidden">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-blue-50 hover:bg-blue-100 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <Zap className="w-3.5 h-3.5 text-blue-600" />
          <span className="text-xs font-semibold text-blue-800">Top Risk-Reduction Actions</span>
          <span className="text-[10px] bg-blue-100 text-blue-700 border border-blue-200 rounded-full px-1.5 py-0.5 font-semibold">
            {actions.length} actions
          </span>
        </div>
        {open ? <ChevronUp className="w-3.5 h-3.5 text-blue-400" /> : <ChevronDown className="w-3.5 h-3.5 text-blue-400" />}
      </button>
      {open && (
        <div className="bg-white px-4 py-3 space-y-1.5">
          <p className="text-[10px] text-gray-400 mb-2">
            Ranked by: <span className="font-medium text-gray-600">Exposure Reduction Efficiency</span> — financial exposure eliminated per analyst hour. Hover an action for the exact figure.
          </p>
          {actions.map((a, i) => (
            <ActionRow key={i} a={a} rank={i + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── P4: FTE vs Risk Curve ─────────────────────────────────────────────────────
function FteCurvePanel({ points, uniqueFindings, totalFindings }) {
  const [open, setOpen] = useState(false);
  if (!points?.length) return null;
  return (
    <div className="mb-4 rounded-xl border border-purple-200 overflow-hidden">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-purple-50 hover:bg-purple-100 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <BarChart2 className="w-3.5 h-3.5 text-purple-600" />
          <span className="text-xs font-semibold text-purple-800">FTE vs Risk Reduction Curve</span>
        </div>
        {open ? <ChevronUp className="w-3.5 h-3.5 text-purple-400" /> : <ChevronDown className="w-3.5 h-3.5 text-purple-400" />}
      </button>
      {open && (
        <div className="bg-white px-4 py-3">
          <p className="text-[10px] text-gray-400 mb-3">
            How much financial risk exposure is eliminated as you add analyst capacity. Sorted by highest Exposure Reduction Efficiency (exposure eliminated per analyst hour).
          </p>
          <div className="space-y-1.5">
            {points.filter(p => p.fteAllocated > 0).map((p, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-14 text-right text-xs font-semibold text-gray-700 flex-shrink-0">
                  {fmt(p.fteAllocated)} FTE
                </div>
                <div className="flex-1">
                  <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-purple-500 rounded-full transition-all"
                      style={{ width: `${Math.min(100, p.exposureReducedPct)}%` }}
                    />
                  </div>
                </div>
                <div className="w-24 text-right flex-shrink-0">
                  <span className="text-xs font-bold text-purple-700">{fmt(p.exposureReducedPct)}% risk</span>
                  <div className="text-[10px] text-gray-400">{fmtM(p.exposureReduced)}</div>
                </div>
                <div className="w-20 text-right flex-shrink-0">
                  <span className="text-[10px] text-gray-500">{p.findingsAddressed} findings</span>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-gray-400 mt-2 pt-2 border-t border-gray-100">
            Both FTE demand and this curve are based on{" "}
            <span className="font-medium text-gray-600">{(uniqueFindings ?? 0).toLocaleString()} unique findings</span>
            {totalFindings != null && uniqueFindings != null && totalFindings > uniqueFindings
              ? ` (${totalFindings.toLocaleString()} raw findings after deduplication).`
              : "."}
            {" "}Deduplicated findings share the same analyst remediation effort.
          </p>
        </div>
      )}
    </div>
  );
}

// ── P5: Optimal Allocation ────────────────────────────────────────────────────
function AllocationPanel({ allocation }) {
  const [open, setOpen] = useState(false);
  if (!allocation?.skills?.length) return null;
  // Compute actual allocated FTE from the visible skill rows (not the budget ceiling)
  const actualAllocated = allocation.skills.reduce((sum, s) => sum + (s.allocatedFte ?? 0), 0);
  return (
    <div className="mb-4 rounded-xl border border-teal-200 overflow-hidden">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-teal-50 hover:bg-teal-100 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <Users className="w-3.5 h-3.5 text-teal-600" />
          <span className="text-xs font-semibold text-teal-800">
            Optimal {fmt(actualAllocated)} FTE Allocation
          </span>
          <span
            className="text-[10px] bg-teal-100 text-teal-700 border border-teal-200 rounded-full px-1.5 py-0.5 font-semibold cursor-help"
            title="Exposure-weighted risk reduction: % of total financial exposure addressed by this allocation. Different from Coverage% which measures workload fraction."
          >
            {fmt(allocation.totalRiskReductionPct)}% exposure reduced
          </span>
        </div>
        {open ? <ChevronUp className="w-3.5 h-3.5 text-teal-400" /> : <ChevronDown className="w-3.5 h-3.5 text-teal-400" />}
      </button>
      {open && (
        <div className="bg-white px-4 py-3">
          <p className="text-[10px] text-gray-400 mb-2">
            Greedy allocation: each 0.5 FTE increment goes to the skill area with the highest marginal
            exposure reduction per FTE. Capped at each skill's required FTE.
            Eliminates <span className="font-semibold text-teal-700">{fmtM(allocation.totalExposureHandled)}</span> of
            upper-bound exposure.
          </p>
          <p className="text-[10px] text-amber-700 mb-3 leading-relaxed">
            <span className="font-semibold">Note:</span>{" "}
            Exposure reduction is calculated against the upper-bound scenario estimate.
            Skill areas with zero estimated financial exposure are excluded from optimization (they have no exposure to reduce).
            If a skill shows 0 FTE allocated but 100% coverage in the skill table, it is already adequately covered by
            your configured team — no additional allocation is needed.
            Skill areas where remaining workload has low marginal exposure per FTE are naturally deprioritised
            by the algorithm even if they still have unmet demand.
          </p>
          <div className="space-y-2">
            {allocation.skills.map((s, i) => (
              <div key={i} className="flex items-center gap-3 rounded-lg bg-teal-50 border border-teal-100 px-3 py-2">
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-gray-900">{s.skillLabel}</div>
                  <div className="text-[10px] text-gray-500">
                    {fmt(s.coverageAfter)}% coverage · {fmtM(s.marginalExposure)} handled
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-sm font-bold text-teal-700">{fmt(s.allocatedFte)} FTE</div>
                  <div className="text-[10px] text-gray-400">of {fmt(s.requiredFte)} req.</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Predictive "Cost of Delay" Outlook ───────────────────────────────────────
function OutlookPanel({ outlook, gapMode }) {
  const [open, setOpen] = useState(false);
  if (!outlook) return null;
  const growing = outlook.backlogEnd > outlook.backlogStart;
  return (
    <div className="mb-4 rounded-xl border border-rose-200 overflow-hidden">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-rose-50 hover:bg-rose-100 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-3.5 h-3.5 text-rose-600" />
          <span className="text-xs font-semibold text-rose-800">
            Cost of Delay — cost of committing to a {outlook.horizonDays}-day timeline
          </span>
        </div>
        {open ? <ChevronUp className="w-3.5 h-3.5 text-rose-400" /> : <ChevronDown className="w-3.5 h-3.5 text-rose-400" />}
      </button>
      {open && (
        <div className="bg-white px-4 py-3">
          {gapMode && outlook.bottleneckSkillLabel && (
            <div className="flex items-center gap-1.5 mb-2 text-[11px] font-semibold text-rose-700">
              <AlertTriangle className="w-3 h-3 flex-shrink-0" />
              Bottleneck: {outlook.bottleneckSkillLabel} — demand {fmt(outlook.responseDelayMultiplier)}× capacity (first to fall behind)
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
            <div className="rounded-lg bg-rose-50/60 border border-rose-100 px-3 py-2">
              <div className="text-[10px] text-gray-500 mb-0.5">Open Backlog</div>
              <div className={`text-sm font-bold ${growing ? "text-rose-700" : "text-emerald-700"}`}>
                {outlook.backlogStart} → {outlook.backlogEnd}
              </div>
              <div className="text-[10px] text-gray-400">
                {growing ? "growing — team can't hold the pace" : "cleared over the timeline"}
              </div>
            </div>
            <div className="rounded-lg bg-rose-50/60 border border-rose-100 px-3 py-2">
              <div className="text-[10px] text-gray-500 mb-0.5">Expected Incidents</div>
              <div className="text-sm font-bold text-rose-700">~{outlook.expectedIncidents}</div>
              <div className="text-[10px] text-gray-400">open risks turning into incidents</div>
            </div>
            <div className="rounded-lg bg-rose-50/60 border border-rose-100 px-3 py-2">
              <div className="text-[10px] text-gray-500 mb-0.5">Cost of Delay</div>
              <div className="text-sm font-bold text-rose-700">+{fmtM(outlook.costOfDelayUsd)}</div>
              <div className="text-[10px] text-gray-400">
                slice of Financial Exposure realized over the timeline
                {gapMode && outlook.shortfallCostUsd > 0 ? ` · +${fmtM(outlook.shortfallCostUsd)} from your shortfall` : ""}
              </div>
            </div>
          </div>
          <p className="text-[10px] text-gray-400">
            The cost of committing to this timeline: while you clear the backlog by day {outlook.horizonDays},
            open findings age and a share convert to incidents, so a portion of your Financial Exposure is
            realized before you fix them — a longer timeline defers risk longer, so Cost of Delay grows.
            It is always a <span className="font-medium text-gray-500">slice of Financial Exposure</span> (the
            do-nothing total){gapMode ? "; the shortfall figure is the extra you eat because your team can't hold this pace (see Bottleneck)" : ""}.
            {" "}Distinct from Required Capacity (the team to clear the backlog by the deadline).
          </p>
        </div>
      )}
    </div>
  );
}

// ── Optimization Suggestions Engine ──────────────────────────────────────────

const SUGGESTION_CONFIG = {
  Reallocation: {
    icon:  ArrowRightLeft,
    bg:    "bg-teal-50",
    border:"border-teal-200",
    text:  "text-teal-800",
    badge: "bg-teal-100 text-teal-700 border-teal-200",
    label: "Reallocation",
  },
  Hiring: {
    icon:  UserPlus,
    bg:    "bg-violet-50",
    border:"border-violet-200",
    text:  "text-violet-800",
    badge: "bg-violet-100 text-violet-700 border-violet-200",
    label: "Hire",
  },
  AvoidHiring: {
    icon:  ShieldCheck,
    bg:    "bg-emerald-50",
    border:"border-emerald-200",
    text:  "text-emerald-800",
    badge: "bg-emerald-100 text-emerald-700 border-emerald-200",
    label: "Avoid Hiring",
  },
};

function OptimizationSuggestionsPanel({ suggestions, bottleneckLabel, bottleneckUtilPct, overstaffedLabel, overstaffedSurplus }) {
  const [activeTab, setActiveTab] = useState(null);
  if (!suggestions?.length) return null;

  // Build one tab per suggestion type (only types that have items)
  const groups = [
    { id: "Reallocation", label: "Reallocation" },
    { id: "AvoidHiring",  label: "Avoid Hiring"  },
    { id: "Hiring",       label: "Hiring"         },
  ]
    .map(g => ({ ...g, items: suggestions.filter(s => s.type === g.id) }))
    .filter(g => g.items.length > 0);

  const currentTab = activeTab ?? groups[0]?.id;
  const activeGroup = groups.find(g => g.id === currentTab) ?? groups[0];

  return (
    <div className="mb-4 rounded-xl border border-indigo-200 overflow-hidden">
      {/* Section header */}
      <div className="flex items-center gap-2 px-4 py-2.5 bg-indigo-50 border-b border-indigo-100">
        <ArrowRightLeft className="w-3.5 h-3.5 text-indigo-600 flex-shrink-0" />
        <span className="text-xs font-semibold text-indigo-800">Workforce Optimization Suggestions</span>
        <span className="text-[10px] text-indigo-400 ml-auto">auto-generated from utilization data</span>
      </div>

      {/* Chrome-style tab bar */}
      <div className="flex items-end gap-0.5 bg-slate-100 border-b border-gray-200 px-2 pt-1.5">
        {groups.map(g => {
          const isActive = currentTab === g.id;
          const cfg = SUGGESTION_CONFIG[g.id] || SUGGESTION_CONFIG.Hiring;
          return (
            <button
              key={g.id}
              onClick={() => setActiveTab(g.id)}
              className={`relative flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-t-lg -mb-px transition-all duration-150 focus:outline-none border ${
                isActive
                  ? "bg-white border-gray-200 text-gray-900 z-10 shadow-[0_-1px_4px_rgba(0,0,0,0.07)]"
                  : "bg-transparent border-transparent text-gray-500 hover:bg-white/60 hover:text-gray-700"
              }`}
              style={isActive ? { borderBottomColor: "#ffffff" } : {}}
            >
              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                g.id === "Reallocation" ? "bg-teal-500"
                : g.id === "AvoidHiring" ? "bg-emerald-500"
                : "bg-violet-500"
              }`} />
              {g.label}
              {g.items.length > 1 && (
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full border ml-0.5 ${cfg.badge}`}>
                  {g.items.length}
                </span>
              )}
            </button>
          );
        })}
        <div className="flex-1 self-end border-b border-gray-200 h-px" />
      </div>

      {/* Tab content */}
      <div className="bg-white px-4 py-3 space-y-3">
        {/* Utilization snapshot — only on first tab */}
        {currentTab === groups[0]?.id && (bottleneckLabel || overstaffedLabel) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {bottleneckLabel && (
              <div className="flex items-center gap-2 rounded-lg bg-red-50 border border-red-100 px-3 py-2">
                <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-[10px] font-semibold text-red-700 uppercase tracking-wider">Bottleneck</div>
                  <div className="text-xs font-bold text-red-800 truncate">{bottleneckLabel}</div>
                  <div className="text-[10px] text-red-600">{bottleneckUtilPct?.toFixed(0)}% utilization — Critical strain</div>
                </div>
              </div>
            )}
            {overstaffedLabel && (
              <div className="flex items-center gap-2 rounded-lg bg-sky-50 border border-sky-100 px-3 py-2">
                <span className="w-2 h-2 rounded-full bg-sky-500 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-[10px] font-semibold text-sky-700 uppercase tracking-wider">Overstaffed</div>
                  <div className="text-xs font-bold text-sky-800 truncate">{overstaffedLabel}</div>
                  <div className="text-[10px] text-sky-600">{overstaffedSurplus?.toFixed(1)} FTE surplus available</div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Active tab suggestions */}
        {activeGroup?.items.map((s, i) => {
          const cfg  = SUGGESTION_CONFIG[s.type] || SUGGESTION_CONFIG.Hiring;
          const Icon = cfg.icon;
          return (
            <div key={i} className={`rounded-lg border ${cfg.border} ${cfg.bg} px-3 py-2.5`}>
              <div className="flex items-start gap-2.5">
                <Icon className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${cfg.text}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${cfg.badge}`}>
                      {cfg.label}
                    </span>
                    {s.fteAmount > 0 && (
                      <span className={`text-xs font-bold ${cfg.text}`}>{s.fteAmount.toFixed(1)} FTE</span>
                    )}
                    {s.type === "Reallocation" && s.fromSkillLabel && s.toSkillLabel && (
                      <span className="text-[10px] text-gray-500">{s.fromSkillLabel} → {s.toSkillLabel}</span>
                    )}
                    {s.type === "Hiring" && s.toSkillLabel && (
                      <span className="text-[10px] text-gray-500">for {s.toSkillLabel}</span>
                    )}
                  </div>
                  <p className={`text-xs leading-relaxed ${cfg.text}`}>{s.rationale}</p>
                  {s.exposureImpact > 0 && (
                    <p className="text-[10px] text-gray-400 mt-0.5">
                      Estimated exposure impact: <span className="font-semibold text-gray-600">{fmtM(s.exposureImpact)}</span>
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function WieExecutiveHeader({ executive, mode, horizonDays, totalFindings, uniqueFindings, duplicatesRemoved, skills = [], methodologyVersion, parametersJson }) {
  if (!executive) return null;

  const conf    = CONF_CFG[executive.overallConfidence] || CONF_CFG.LOW;
  const gapMode = mode === "GAP_ANALYSIS";

  // Coverage badge colouring
  const covPct  = executive.coveragePercent ?? 0;
  const covColor = covPct >= 80 ? "text-emerald-700" : covPct >= 50 ? "text-amber-700" : "text-red-700";

  // Confidence badge for KPI cards
  const confBadgeCls = conf.bg + " " + conf.text + " " + (executive.overallConfidence === "HIGH" ? "border-emerald-200" : executive.overallConfidence === "MEDIUM" ? "border-amber-200" : "border-gray-200");

  // Risk-Weighted Coverage tooltip
  const rwcTooltip = "Risk-Weighted Coverage reflects the percentage of total financial risk exposure being mitigated, not just the percentage of findings resolved. Higher-risk findings contribute more weight. Lower than raw coverage when high-exposure skill areas are understaffed.";

  // Total available FTE across all configured skills (for Gap Analysis headline)
  const totalAvailableFte = skills.reduce((sum, s) => sum + (s.availableFte || 0), 0);

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 mb-4">
      {/* Mode banner */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-bold text-gray-900">
            Workforce Intelligence
            <span className="ml-2 text-xs font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
              {horizonDays}-Day Timeline
            </span>
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {gapMode
              ? "Gap Analysis — comparing risk workload against current team capacity"
              : "Workload Only — security workload required to address open risks"}
          </p>
          {/* P1: Deduplication banner */}
          {duplicatesRemoved > 0 && (
            <p className="text-[10px] text-gray-400 mt-0.5">
              <span className="font-semibold text-gray-600">{uniqueFindings?.toLocaleString()}</span> unique findings
              ({duplicatesRemoved} duplicates merged across modules · raw total: {totalFindings?.toLocaleString()})
            </p>
          )}
          {/* Under-staffing nudge → points to the Cost of Delay outlook */}
          {gapMode && executive.understaffed && (
            <p className="text-[10px] font-semibold text-rose-600 mt-1 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Understaffed — backlog grows at current capacity; see Cost of Delay below
            </p>
          )}
        </div>
        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1.5 ${conf.bg} ${conf.text}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${conf.dot}`} />
          {executive.overallConfidence} confidence
        </span>
      </div>
      {/* KPI cards — Dashboard-style with gradient accent bar */}
      <div className="grid gap-3 mb-5 grid-cols-2 md:grid-cols-4">

        {gapMode ? (
          /* Gap Analysis: lead with Available (what you have), then Coverage, Gap, Exposure */
          (<>
            <WieKpiCard
              label="Team Capacity"
              value={<>{fmt(totalAvailableFte)}<span className="text-lg font-semibold ml-1">FTE</span></>}
              sub={`vs ${fmt(executive.totalRequiredFte)} FTE required`}
              color="text-emerald-700"
              badge={{ cls: confBadgeCls, label: `${executive.overallConfidence} confidence` }}
            />
            <WieKpiCard
              label="Skill-Matched Coverage"
              value={<>{fmt(covPct)}<span className="text-lg font-semibold ml-0.5">%</span></>}
              sub="workload covered after skill constraints"
              color={covColor}
              progress={covPct}
              tooltip="Coverage is calculated per skill area, not by total headcount. Surplus capacity in one domain cannot substitute for a shortage in another (e.g., extra SecOps staff cannot close an IAM gap)."
            />
            <WieKpiCard
              label="Gap"
              value={<>{fmt(executive.totalGapFte)}<span className="text-lg font-semibold ml-1">FTE</span></>}
              sub="additional capacity needed"
              color={executive.totalGapFte > 0 ? "text-red-700" : "text-emerald-700"}
              badge={
                (executive.riskWeightedCoveragePercent ?? 0) > 0
                  ? { cls: "bg-gray-100 text-gray-600 border-gray-200", label: `${fmt(executive.riskWeightedCoveragePercent)}% risk-wtd` }
                  : undefined
              }
              tooltip={rwcTooltip}
            />
          </>)
        ) : (
          /* Workload Only: lead with Required FTE (the workload demand) */
          (<WieKpiCard
            label="Required Capacity"
            value={<>{fmt(executive.totalRequiredFte)}<span className="text-lg font-semibold ml-1">FTE</span></>}
            sub={`to clear within ${horizonDays} days`}
            color="text-blue-700"
            badge={{ cls: confBadgeCls, label: `${executive.overallConfidence} confidence` }}
            tooltip={`Sustained team to clear the open backlog within ${horizonDays} days = Σ(effort ÷ min(window, severity SLA)) ÷ 109 h/mo. Higher than Total Effort ÷ window because urgent findings are capped to their own SLA (Critical 1mo, High 2mo) and can't use the full window.`}
          />)
        )}

        {/* Total effort to clear — one-time backlog burn-down (person-months). Workload Only:
            in Gap Analysis the headline leads with Team Capacity / Coverage / Gap instead. */}
        {!gapMode && (
          <WieKpiCard
            label="Total Effort to Clear"
            value={<>{fmt((executive.totalEffortHours ?? 0) / 109)}<span className="text-lg font-semibold ml-1">FTE-months</span></>}
            sub="one-time backlog burn-down"
            color="text-indigo-700"
            tooltip={`${fmt(executive.totalEffortHours ?? 0)} h of one-time remediation effort to fully clear the open backlog. Distinct from Required Capacity (the sustained team needed to keep pace).`}
          />
        )}

        {/* Cost of Delay — absolute slice of Financial Exposure in both modes (grows with the timeline);
            Gap adds the under-staffing portion as a secondary line. */}
        <WieKpiCard
          label="Cost of Delay"
          value={!executive.outlook ? <span className="text-gray-400">—</span> : <span>+{fmtM(executive.outlook.costOfDelayUsd)}</span>}
          sub={
            gapMode && (executive.outlook?.shortfallCostUsd ?? 0) > 0
              ? `incl. +${fmtM(executive.outlook.shortfallCostUsd)} from under-staffing`
              : "slice of Financial Exposure over the timeline"
          }
          color="text-rose-700"
          tooltip="The slice of Financial Exposure that materializes while you clear the backlog over this timeline (a longer timeline ⇒ more); always ≤ Financial Exposure (the do-nothing total). In Gap Analysis it uses your team's pace — the 'from under-staffing' portion is the extra beyond a fully-resourced plan. See the Cost of Delay panel below."
        />

        {/* Financial Exposure — Workload Only (Gap Analysis surfaces Delay Tax in its place) */}
        {!gapMode && (
          <WieKpiCard
            label="Financial Exposure"
            value={<span className="text-2xl">{fmtM(executive.exposureLow)}–{fmtM(executive.exposureHigh)}</span>}
            sub="if workload unresolved"
            color="text-orange-700"
            badge={
              executive.totalRequiredBudget > 0
                ? { cls: "bg-violet-100 text-violet-700 border-violet-200", label: `${fmtM(executive.totalRequiredBudget)} team budget` }
                : undefined
            }
          />
        )}
      </div>
      {/* Workload distribution strip */}
      <WorkloadDistributionStrip skills={skills} mode={mode} />
      {/* FTE bar chart */}
      <FteBarChart skills={skills} mode={mode} />
      {/* Cost of Delay outlook (both modes) — now also carries the bottleneck / response-delay
          signal that used to live in the separate Capacity Breach Impact panel. */}
      {executive.outlook && <OutlookPanel outlook={executive.outlook} gapMode={gapMode} />}
      {/* P3: Top Risk-Reduction Actions */}
      <TopActionsPanel actions={executive.topRiskActions} />
      {/* P4: FTE vs Risk Curve */}
      <FteCurvePanel
        points={executive.fteCurvePoints}
        uniqueFindings={uniqueFindings}
        totalFindings={totalFindings}
      />
      {/* P5: Optimal Allocation */}
      <AllocationPanel allocation={executive.optimalAllocation} />
      {/* Optimization Suggestions — Gap Analysis only (reallocation/hiring require capacity data) */}
      {gapMode && <OptimizationSuggestionsPanel
        suggestions={executive.optimizationSuggestions}
        bottleneckLabel={executive.bottleneckSkillLabel}
        bottleneckUtilPct={executive.bottleneckUtilizationPct}
        overstaffedLabel={executive.overstaffedSkillLabel}
        overstaffedSurplus={executive.overstaffedSurplusFte}
      />}
      {/* Overall confidence path to HIGH */}
      {executive.overallConfidence !== "HIGH" && (
        <div className="mb-4">
          <div className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
            Overall Confidence — Path to HIGH
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 space-y-2">
            {[
              { met: true,  label: "Sufficient open findings across skill areas" },
              { met: executive.overallConfidence === "HIGH",
                label: "Team headcount (FTE) configured for 3+ skill areas",
                detail: "Go to Gap Analysis → Team Setup and enter current FTE for your top skill areas" },
            ].map(c => (
              <div key={c.label} className="flex items-start gap-2.5">
                <span className={`mt-0.5 flex-shrink-0 text-sm font-bold ${c.met ? "text-emerald-500" : "text-gray-300"}`}>
                  {c.met ? "✓" : "○"}
                </span>
                <div>
                  <span className={`text-xs font-semibold ${c.met ? "text-gray-700" : "text-gray-400"}`}>
                    {c.label}
                  </span>
                  {!c.met && c.detail && (
                    <p className="text-xs text-amber-700 mt-0.5">{c.detail}</p>
                  )}
                </div>
              </div>
            ))}
            <p className="text-[10px] text-gray-400 pt-1 border-t border-gray-200">
              Scoring: 3 dimensions (33% each) — data completeness · skill mapping · capacity configured.
              HIGH ≥85% · MEDIUM ≥45% · LOW &lt;45% · Historical validation shown as separate ✓ Validated badge.
            </p>
          </div>
        </div>
      )}
      {/* Top 3 Workload Drivers */}
      {executive.topDrivers?.length > 0 && (
        <div className="mb-3">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
            Top Workload Drivers
          </div>
          <div className="space-y-1.5">
            {executive.topDrivers.slice(0, 3).map((d, i) => (
              <div key={i} className="flex items-center gap-3 bg-gray-50 rounded-lg px-3 py-2">
                <span className="w-5 h-5 rounded-full bg-gray-200 text-gray-600 text-xs font-bold flex items-center justify-center flex-shrink-0">
                  {i + 1}
                </span>
                <span className="flex-1 text-xs font-medium text-gray-800 truncate min-w-0">{d.riskTitle}</span>
                {d.pctOfTotal > 0 && (
                  <span className="text-xs font-semibold text-gray-500 flex-shrink-0">
                    {fmt(d.pctOfTotal)}%
                  </span>
                )}
                <span className="text-xs font-semibold text-blue-700 flex-shrink-0 tabular-nums">{fmt(d.hours)}h</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <MethodologyPanel executive={executive} methodologyVersion={methodologyVersion} parametersJson={parametersJson} />
    </div>
  );
}
